// ======================================================================
// File: src/modules/ppe/pages/procurement/grn/view/GRNDetail.jsx
// Module: PPE → Procurement → GRN
//
// Purpose:
// - Detailed view of a single GRN
// - Shows header and line items
// - Provides workflow actions (submit/approve/post/cancel)
// ======================================================================

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  formatDate,
  formatQuantity
} from "../../../../../../utils/formatUtils";

import grnService from "../services/grnService";
import GRNLineDrawer from "../form/GRNLineDrawer";

export default function GRNDetail() {

  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [lineDrawerOpen, setLineDrawerOpen] = useState(false);

  // ----------------------------------------------------------
  // LOAD GRN DETAIL
  // ----------------------------------------------------------
  const loadDetail = async () => {
    try {
      setLoading(true);
      const result = await grnService.getGRNById(id);

      if (result && result.data) {
        setData(result.data);
      } else {
        setData(null);
      }

    } catch (error) {
      alert("Failed to load GRN details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetail();
    // eslint-disable-next-line
  }, [id]);

  // ----------------------------------------------------------
  // WORKFLOW ACTIONS
  // ----------------------------------------------------------
  const handleSubmit = async () => {
    try {
      await grnService.submitGRN(id);
      alert("GRN submitted successfully");
      loadDetail();
    } catch (error) {
      alert(error?.response?.data?.message || "Submit failed");
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      await grnService.submitForApproval(id);

      alert("GRN submitted for approval successfully");

      loadDetail();
    } catch (error) {
      alert(
        error?.response?.data?.message ||
        "Submit for approval failed"
      );
    }
  };
  const handleApprove = async () => {
    try {
      await grnService.approveGRN(id);
      alert("GRN approved successfully");
      loadDetail();
    } catch (error) {
      alert(error?.response?.data?.message || "Approve failed");
    }
  };

  const handlePost = async () => {
    try {
      await grnService.postGRN(id);
      alert("GRN posted successfully");
      loadDetail();
    } catch (error) {
      alert(error?.response?.data?.message || "Post failed");
    }
  };

  const handleCancel = async () => {
    const reason = prompt("Enter cancellation reason");

    if (!reason) return;

    try {
      await grnService.cancelGRN(id, reason);
      alert("GRN cancelled successfully");
      loadDetail();
    } catch (error) {
      alert(error?.response?.data?.message || "Cancel failed");
    }
  };

  // ----------------------------------------------------------
  // RENDER HELPERS
  // ----------------------------------------------------------
  const renderActions = () => {
    if (!data) return null;

    return (
      <div style={{ display: "flex", gap: 10 }}>

        {data.status === "DRAFT" && (
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!data.lines || data.lines.length === 0}
          >
            Submit
          </button>
        )}

        
        {data.status === "SUBMITTED" && (
            <button
              className="btn btn-success"
              onClick={handleSubmitForApproval}
            >
              Submit For Approval
            </button>
          )}


        {data.status === "APPROVED" && (
          <button
            className="btn btn-warning"
            onClick={handlePost}
          >
            Post GRN
          </button>
        )}

        {data.status === "POSTED" && (
          <button
            className="btn btn-danger"
            onClick={handleCancel}
          >
            Cancel GRN
          </button>
        )}

        {data.status === "DRAFT" && (
          <button
            className="btn btn-secondary"
            onClick={() => setLineDrawerOpen(true)}
          >
            + Add Line
          </button>
        )}

      </div>
    );
  };

  // ----------------------------------------------------------
  // MAIN RENDER
  // ----------------------------------------------------------
  if (loading) {
    return <div>Loading...</div>;
  }

  if (!data) {
    return <div>No GRN data found</div>;
  }

  return (
    <div>

      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div>
          <h4>GRN Detail – {data.grn_no}</h4>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            Status: {data.status}
          </div>
        </div>

        <button
          className="btn btn-light"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>

      {/* ACTION BAR */}
      <div style={{ marginBottom: 20 }}>
        {renderActions()}
      </div>

      {/* HEADER INFO CARD */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row">

            <div className="col-3">
              <strong>GRN Date:</strong>
              <div>{formatDate(data.grn_date)}</div>
            </div>

            <div className="col-3">
            <strong>Vendor:</strong>
            <div>{data.vendor_name || data.vendor_id}</div>
          </div>

          <div className="col-3">
            <strong>Warehouse:</strong>
            <div>{data.warehouse_name || data.warehouse_id}</div>
          </div>

          <div className="col-3">
            <strong>PO:</strong>
            <div>{data.po_no || data.po_id}</div>
          </div>


            <div className="col-3 mt-3">
              <strong>Invoice No:</strong>
              <div>{data.invoice_no}</div>
            </div>

            <div className="col-3 mt-3">
              <strong>Invoice Date:</strong>
              <div>{formatDate(data.invoice_date)}</div>
            </div>

          </div>
        </div>
      </div>

      {/* LINE ITEMS */}
      <div className="card">
        <div className="card-header">
          <strong>GRN Lines</strong>
        </div>

        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>Material</th>
                <th>Qty</th>
                <th>UOM</th>
                <th>Rate</th>
                <th>Batch</th>
                <th>Expiry</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {data.lines && data.lines.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center text-muted"
                  >
                    No lines added
                  </td>
                </tr>
              )}

              {data.lines &&
              data.lines.map((l) => (
                <tr key={l.id}>
                  <td>{l.material_name || l.material_id}</td>
                  <td>{formatQuantity(l.received_qty)}</td>
                  <td>{l.uom}</td>
                  <td>{formatQuantity(l.unit_price)}</td>
                  <td>{l.batch_no}</td>
                  <td>{formatDate(l.expiry_date)}</td>
                  <td>{l.status}</td>
                </tr>
            ))}

            </tbody>
          </table>
        </div>
      </div>

      {/* LINE DRAWER */}
      <GRNLineDrawer
        open={lineDrawerOpen}
        grnId={id}
        poId={data.po_id}
        onClose={() => setLineDrawerOpen(false)}
        onSaved={loadDetail}
      />

    </div>
  );
}
